import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CiCircleMinus } from 'react-icons/ci';
import {
  MdFolderOpen,
  MdOutlineAdminPanelSettings,
  MdOutlineBackHand,
  MdOutlineDashboard,
  MdOutlineFeaturedPlayList,
  MdOutlineGroups,
  MdOutlineLocationOn,
  MdOutlinePeople,
  MdOutlineShoppingBag,
  MdOutlineReceiptLong,
  MdOutlineAddCircleOutline,
  MdOutlineQueryBuilder,
  MdOutlineAssignment,
} from 'react-icons/md';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import Logo from '../assets/manak-logo.png';
import MiniLogo from '../assets/mini-logo.png';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { loadProjects } from '../redux/actions/projectAction';
import { hasManageEmployeePermission, hasBillProjectPermission } from '../utils/permissionUtils';

interface SidebarProps {
  className?: string;
  variant?: 'full' | 'mini';
  onNavClick?: () => void; // Add callback for handling navigation clicks
}

export const Sidebar: React.FC<SidebarProps> = ({ className, variant = 'full', onNavClick }) => {
  const [tooltipData, setTooltipData] = useState<{
    show: boolean;
    top: number;
    name: string;
    section?: string;
  }>({ show: false, top: 0, name: '' });

  const [showDevAlert, setShowDevAlert] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Get user data and permissions from auth state
  const { user } = useAppSelector(state => state.auth);

  // Determine if user has permissions
  const [hasRolePermission, setHasRolePermission] = useState(true);
  const [hasProjectPermission, setHasProjectPermission] = useState(true);
  const [hasCustomerPermission, setHasCustomerPermission] = useState(true);
  const [hasEmployeePermission, setHasEmployeePermission] = useState(true);
  const [hasManageEmployeeAbility, setHasManageEmployeeAbility] = useState(true);
  const [hasBillingPermission, setHasBillingPermission] = useState(true);
  const [hasTaskSheetPermission, setHasTaskSheetPermission] = useState(false);

  // Initialize permission state from user data
  useEffect(() => {
    if (user) {
      // Task sheet is only for admin/superAdmin
      setHasTaskSheetPermission(user.role === 'ADMIN' || user.designation === 'superAdmin');

      // Super Admin has all permissions
      if (user.designation === 'superAdmin') {
        setHasRolePermission(true);
        setHasProjectPermission(true);
        setHasCustomerPermission(true);
        setHasEmployeePermission(true);
        setHasManageEmployeeAbility(true);
        setHasBillingPermission(true);
        setHasTaskSheetPermission(true);
      } else if (user.permissions) {
        console.log('User permissions for sidebar check:', user.permissions);

        // Check if permissions is an object with numeric keys (like {0: {...}})
        if (typeof user.permissions === 'object' && '0' in user.permissions) {
          //@ts-ignore
          setHasRolePermission(Boolean(user.permissions[0].roleRead));
          //@ts-ignore
          setHasProjectPermission(Boolean(user.permissions[0].projectRead));
          //@ts-ignore
          setHasCustomerPermission(Boolean(user.permissions[0].customerRead));
          //@ts-ignore
          setHasEmployeePermission(Boolean(user.permissions[0].employeeRead));
          //@ts-ignore
          setHasManageEmployeeAbility(Boolean(user.permissions[0].canManageEmployee));
          //@ts-ignore
          setHasBillingPermission(Boolean(user.permissions[0].canBillProject));
        }
        // Check if permissions is an array
        else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
          setHasRolePermission(Boolean(user.permissions[0].roleRead));
          setHasProjectPermission(Boolean(user.permissions[0].projectRead));
          setHasCustomerPermission(Boolean(user.permissions[0].customerRead));
          setHasEmployeePermission(Boolean(user.permissions[0].employeeRead));
          setHasManageEmployeeAbility(Boolean(user.permissions[0].canManageEmployee));
          setHasBillingPermission(Boolean(user.permissions[0].canBillProject));
        }
        // Direct object access if neither of the above
        else if (typeof user.permissions === 'object') {
          setHasRolePermission(Boolean(user.permissions.roleRead));
          setHasProjectPermission(Boolean(user.permissions.projectRead));
          setHasCustomerPermission(Boolean(user.permissions.customerRead));
          setHasEmployeePermission(Boolean(user.permissions.employeeRead));
          setHasManageEmployeeAbility(Boolean(user.permissions.canManageEmployee));
          setHasBillingPermission(hasBillProjectPermission(user));
        } else {
          setHasRolePermission(false);
          setHasProjectPermission(false);
          setHasCustomerPermission(false);
          setHasEmployeePermission(false);
          setHasManageEmployeeAbility(false);
          setHasBillingPermission(false);
        }
      } else {
        // No permissions object means no access
        setHasRolePermission(false);
        setHasProjectPermission(false);
        setHasCustomerPermission(false);
        setHasEmployeePermission(false);
        setHasManageEmployeeAbility(false);
        setHasBillingPermission(false);
      }
    } else {
      // Default to no permission if user is not defined
      setHasRolePermission(false);
      setHasProjectPermission(false);
      setHasCustomerPermission(false);
      setHasEmployeePermission(false);
      setHasManageEmployeeAbility(false);
      setHasBillingPermission(false);
      setHasTaskSheetPermission(false);
    }
  }, [user]);

  // Function to show development alert
  const handleDevModuleClick = () => {
    // Vibrate on mobile devices if supported
    if (navigator.vibrate) {
      navigator.vibrate(200);
    }

    setShowDevAlert(true);
    // Increased timeout to 5 seconds (5000ms)
    setTimeout(() => setShowDevAlert(false), 5000);
  };

  const location = useLocation();
  const isMini = variant === 'mini';
  const isMobile = window.innerWidth < 1024;

  // Function to determine which section is active
  const getActiveSection = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'dashboard';
    if (
      [
        '/projects',
        '/on-hold',
        '/revisions',
        '/employees',
        '/customers',
        '/users',
        '/states',
      ].includes(path)
    )
      return 'manage';
    if (['/attendence', '/task-sheet'].includes(path) || path.startsWith('/task-sheet/employee')) return 'task';
    if (['/billing-history', '/create-invoice'].includes(path)) return 'billing';
    if (path === '/permissions') return 'access';
    return '';
  };

  const activeSection = getActiveSection();

  // Define menu items with their respective paths
  const menuItems = [
    { name: 'Projects', icon: <MdFolderOpen className="text-lg" />, path: '/projects' },
    {
      name: 'My Projects',
      icon: <MdOutlineFeaturedPlayList className="text-lg" />,
      path: '/my-projects',
    },
    { name: 'Customers', icon: <MdOutlineGroups className="text-lg" />, path: '/customers' },
    { name: 'Users', icon: <MdOutlinePeople className="text-lg" />, path: '/users' },
    {
      name: 'Employees',
      icon: <MdOutlineShoppingBag className="text-lg" />,
      path: '/employees',
    },
    {
      name: 'Team Members',
      icon: <MdOutlineGroups className="text-lg" />,
      path: '/team-members',
    },
  ];

  const handleNavClick = () => {
    // Only close sidebar on mobile
    if (isMobile && onNavClick) {
      onNavClick();
    }
  };

  // Simple renderNavLink that doesn't preload data
  const renderNavLink = (icon: JSX.Element, name: string, path: string, section?: string) => {
    // Create a handler for navigation clicks
    const handleNavItemClick = (e: React.MouseEvent) => {
      // Prevent default NavLink behavior
      e.preventDefault();

      // Only close sidebar on mobile if callback exists
      if (isMobile && onNavClick) {
        onNavClick();
      }

      // For billing-history, always navigate to clean URL (reset filters)
      // For other pages, use normal navigation logic
      if (path === '/billing-history') {
        // Always navigate to clean URL without query params
        navigate('/billing-history', { replace: location.pathname === path });
        return;
      }

      // Check if we're already on this path
      if (location.pathname === path) {
        // Force a fresh navigation by navigating to a dummy route then back
        // This ensures location.key changes and triggers data refresh
        navigate('/refresh-temp', { replace: true });
        setTimeout(() => {
          navigate(path, { replace: true });
        }, 0);
      } else {
        // Navigate normally if on a different path
        navigate(path);
      }
    };

    if (isMini) {
      return (
        <NavLink
          to={path}
          className={({ isActive }) =>
            `relative flex justify-center items-center p-2 rounded-lg ${isActive ? 'bg-blue-100 text-blue-900' : 'text-gray-400 hover:bg-gray-100'
            }`
          }
          onClick={handleNavItemClick}
          onMouseEnter={e => {
            const rect = e.currentTarget.getBoundingClientRect();
            const centerY = rect.top + rect.height / 2;
            setTooltipData({ show: true, top: centerY, name, section });
          }}
          onMouseLeave={() => setTooltipData(prev => ({ ...prev, show: false }))}
        >
          {icon}
        </NavLink>
      );
    }

    return (
      <NavLink
        to={path}
        className={({ isActive }) =>
          `flex w-full items-center gap-2 text-sm p-2 rounded-lg ${isActive
            ? 'bg-blue-100 font-semibold text-blue-900'
            : 'text-gray-400 hover:bg-gray-100 font-medium'
          }`
        }
        onClick={handleNavItemClick}
      >
        <div className="w-1/3 flex justify-center">{icon}</div>
        <span className="w-full">{name}</span>
      </NavLink>
    );
  };

  // Function to render under development navigation items
  const renderDevNavLink = (icon: JSX.Element, label: string, section?: string) => {
    const isActive = section === activeSection;

    return (
      <div
        onClick={handleDevModuleClick}
        className={`flex items-center ${isMini ? 'justify-center' : 'justify-start'} w-full ${!isMini ? 'px-4' : ''
          } py-3 ${isActive
            ? 'bg-blue-50 text-blue-900 border-l-4 border-blue-900'
            : 'text-gray-300 hover:bg-blue-50 hover:text-gray-400'
          } transition-colors duration-150 cursor-pointer group`}
      >
        <span
          className={`${!isMini ? 'mr-4' : ''} ${isActive ? 'text-blue-900' : 'text-gray-300'}`}
        >
          {icon}
        </span>
        {!isMini && (
          <div className="flex flex-col">
            <span className="text-sm font-medium">{label}</span>
          </div>
        )}
        {isMini && (
          <div
            className="absolute left-16 z-10 w-auto p-2 min-w-max whitespace-nowrap rounded-md shadow-md
            text-gray-400 bg-white text-xs font-bold opacity-0 group-hover:opacity-100"
          >
            {label}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div
        className={`${className} h-screen bg-white border-r rounded-tr-3xl border-gray-300 ${isMini ? 'p-2' : 'p-4'
          } flex flex-col fixed shadow-md`}
      >
        {/* Logo Section */}
        <div
          className={`flex items-center ${isMini ? 'justify-center h-12 mb-4' : 'h-16 space-x-2 px-5 mb-6'}`}
        >
          <img
            src={isMini ? MiniLogo : Logo}
            alt="manak-logo"
            className={isMini ? 'h-8' : 'h-12'}
          />
        </div>

        {/* Navigation Links */}
        <nav
          className={`flex-1 w-full ${isMini ? 'px-1' : 'px-5'} overflow-y-auto sidebar-custom-scroll`}
        >
          {/* Dashboard Section */}
          {!isMini && (
            <div className="flex w-full justify-center items-center mb-4">
              <p
                className={`text-xs font-bold ${activeSection === 'dashboard' ? 'text-blue-900' : 'text-gray-500'}`}
              >
                DASHBOARD
              </p>
              <span
                className={`w-full bg-gradient-to-r ${activeSection === 'dashboard' ? 'from-blue-900' : 'from-gray-500'} to-white h-[2px] ml-2`}
              ></span>
            </div>
          )}

          {/* Dashboard Link - Always shown for all users */}
          <div className={isMini ? 'relative' : ''}>
            {renderNavLink(
              <MdOutlineDashboard className="text-lg" />,
              'Dashboard',
              '/dashboard',
              'Dashboard'
            )}
            {isMini && (
              <div className="w-8 h-[1px] bg-gradient-to-r from-gray-300 to-transparent mx-auto my-3"></div>
            )}
          </div>

          {/* Manage section - Always visible because "My Projects" should be accessible to everyone */}
          <>
            {/* Manage Section */}
            {!isMini && (
              <div className="flex w-full justify-center items-center my-4">
                <p
                  className={`text-xs font-bold ${activeSection === 'manage' ? 'text-blue-900' : 'text-gray-500'}`}
                >
                  MANAGE
                </p>
                <span
                  className={`w-full bg-gradient-to-r ${activeSection === 'manage' ? 'from-blue-900' : 'from-gray-500'} to-white h-[2px] ml-2`}
                ></span>
              </div>
            )}

            {/* Manage Links */}
            <div className={`${isMini ? 'relative' : ''} flex flex-col gap-2`}>
              {menuItems.map(item => {
                // Hide Projects if user lacks permission
                if (item.name === 'Projects' && !hasProjectPermission) {
                  return null;
                }
                // Always show My Projects
                if (item.name === 'My Projects') {
                  return (
                    <div key={item.path}>
                      {renderNavLink(item.icon, item.name, item.path, 'Manage')}
                    </div>
                  );
                }
                // Check permissions for other items
                if (item.name === 'Non-Active' && !hasProjectPermission) {
                  return null;
                }
                if (item.name === 'Customers' && !hasCustomerPermission) {
                  return null;
                }
                if (
                  (item.name === 'Employees' || item.name === 'Users') &&
                  !hasEmployeePermission
                ) {
                  return null;
                }
                // Hide Team Members if user lacks manage employee permission
                if (item.name === 'Team Members' && !hasManageEmployeePermission(user)) {
                  return null;
                }
                // Render the nav link if permissions allow
                return (
                  <div key={item.path}>
                    {renderNavLink(item.icon, item.name, item.path, 'Manage')}
                  </div>
                );
              })}
              {isMini && (
                <div className="w-8 h-[1px] bg-gradient-to-r from-gray-300 to-transparent mx-auto my-3"></div>
              )}
            </div>
          </>

          {/* Access Control Section - Only shown if user has role permissions */}
          {hasRolePermission && (
            <>
              {!isMini && (
                <div className="flex w-full justify-center items-center mt-5 mb-2">
                  <p
                    className={`text-xs font-bold ${activeSection === 'access' ? 'text-blue-900' : 'text-gray-500'
                      } w-full`}
                  >
                    ACCESS CONTROL
                  </p>
                  <span
                    className={`w-[50%] bg-gradient-to-r ${activeSection === 'access' ? 'from-blue-900' : 'from-gray-500'
                      } to-white h-[2px] ml-2`}
                  ></span>
                </div>
              )}

              {/* Access Control Link */}
              <div className={isMini ? 'relative' : ''}>
                {renderNavLink(
                  <MdOutlineAdminPanelSettings className="text-lg" />,
                  'Permissions',
                  '/permissions',
                  'Access Control'
                )}
                {isMini && (
                  <div className="w-8 h-[1px] bg-gradient-to-r from-gray-300 to-transparent mx-auto my-3"></div>
                )}
              </div>
            </>
          )}

          {/* Task & Attendance Section */}
          {(
            <>
              {!isMini && (
                <div className="flex w-full justify-center items-center mt-5 mb-2">
                  <p
                    className={`text-xs font-bold ${activeSection === 'task' ? 'text-blue-900' : 'text-gray-500'} w-full`}
                  >
                    TASK & ATTENDANCE
                  </p>
                  <span
                    className={`w-[30%] bg-gradient-to-r ${activeSection === 'task' ? 'from-blue-900' : 'from-gray-500'} to-white h-[2px] ml-2`}
                  ></span>
                </div>
              )}

              <div className={`${isMini ? 'relative' : ''} flex flex-col gap-2`}>
                {hasTaskSheetPermission && renderNavLink(
                  <MdOutlineAssignment className="text-lg" />,
                  'Task Sheet',
                  '/task-sheet',
                  'Task & Attendance'
                )}
                {isMini && (
                  <div className="w-8 h-[1px] bg-gradient-to-r from-gray-300 to-transparent mx-auto my-3"></div>
                )}
              </div>
            </>
          )}

          {/* Billing Section */}
          {hasBillingPermission && !isMini && (
            <div className="flex w-full justify-center items-center mt-5 mb-2">
              <p
                className={`text-xs font-bold ${activeSection === 'billing' ? 'text-blue-900' : 'text-gray-500'
                  } w-full`}
              >
                BILLING
              </p>
              <span
                className={`w-full bg-gradient-to-r ${activeSection === 'billing' ? 'from-blue-900' : 'from-gray-500'
                  } to-white h-[2px] ml-2`}
              ></span>
            </div>
          )}

          {/* Billing Links */}
          {hasBillingPermission && (
            <div className={`${isMini ? 'relative' : ''} flex flex-col gap-2`}>
              {renderNavLink(
                <MdOutlineReceiptLong className="text-lg" />,
                'Billing History',
                '/billing-history',
                'Billing'
              )}
              {renderNavLink(
                <MdOutlineAddCircleOutline className="text-lg" />,
                'Create Invoice',
                '/create-invoice',
                'Billing'
              )}
              {isMini && (
                <div className="w-8 h-[1px] bg-gradient-to-r from-gray-300 to-transparent mx-auto my-3"></div>
              )}
            </div>
          )}

          {/* Version indicator and Copyright - moved inside nav */}
          <div className={`mt-auto pt-4 ${isMini ? 'text-center' : ''} pb-2`}>
            {/* Copyright and version removed */}
          </div>
        </nav>
      </div>

      {/* Development Alert Modal */}
      {showDevAlert && (
        <div className="fixed inset-0 flex items-center justify-center z-[99999]">
          <div
            className="absolute inset-0 bg-black opacity-40"
            onClick={() => setShowDevAlert(false)}
          ></div>
          <div className="animate-bounce-once bg-red-50 border-2 border-red-500 text-red-800 p-6 rounded-lg shadow-xl z-50 max-w-md w-11/12 mx-auto relative">
            {/* Close button */}
            <button
              onClick={() => setShowDevAlert(false)}
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-red-200 hover:bg-red-300 text-red-800 transition-colors"
              aria-label="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="flex items-start pt-2 pb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12 mr-4 text-red-600 animate-pulse flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <h3 className="text-xl font-bold mb-2">Module Under Development</h3>
                <p className="text-red-700 text-base">
                  This module is currently being developed and will be available soon!
                </p>
                <p className="text-red-600 text-sm mt-3">
                  Our development team is working diligently to bring you this feature in the next
                  update.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tooltip Portal */}
      {isMini &&
        tooltipData.show &&
        createPortal(
          <div
            className="fixed min-w-[140px] bg-gray-800 text-white text-xs rounded-md shadow-lg transition-all duration-200"
            style={{
              left: '4.5rem', // Using fixed value instead of left-[70px]
              top: `${tooltipData.top}px`,
              transform: 'translateY(-50%)',
              zIndex: 99999,
            }}
          >
            <div className="absolute left-[-6px] top-1/2 -translate-y-1/2">
              <div className="w-0 h-0 border-t-[6px] border-b-[6px] border-r-[6px] border-l-0 border-transparent border-r-gray-800"></div>
            </div>
            <div className="px-3 py-2">
              {tooltipData.section ? (
                <>
                  <div className="text-[10px] text-gray-400 mb-1">{tooltipData.section}</div>
                  <div className="font-medium">{tooltipData.name}</div>
                </>
              ) : (
                <div className="font-medium">{tooltipData.name}</div>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

// Add the bounce animation
const style = document.createElement('style');
style.textContent = `
  @keyframes bounceOnce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }
  .animate-bounce-once {
    animation: bounceOnce 0.5s ease-in-out;
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
  }
  .animate-pulse {
    animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }
`;
document.head.appendChild(style);

export default Sidebar;
